import { newRelicErrorLog } from "../newRelicLogs";

let rmsAccumulator = [];
let lastAudioDetectionTime = Date.now();
let lastAudioTime = Date.now();

export async function startAudioRecorderWorklet(
  audioRecorderHandler,
  dealershipId,
  recordingSessionId = "blank:recordingSessionId"
) {
  try {
    const Navigator = window.navigator || {};

    // simple-audio check
    if (Navigator.mediaDevices) {
      console.log("getUserMedia supported.");
    } else {
      console.error("getUserMedia() not supported on this browser!");
      newRelicErrorLog(
        {},
        {
          dealershipId,
          recordingSessionId,
          originated: "startAudioRecorderWorklet",
        },
        "getUserMedia() not supported on this browser!"
      );
      return [null, null, null, null];
    }

    const audioRecorderContext = new AudioContext({ sampleRate: 16000 });

    console.log("AudioContext sample rate:", audioRecorderContext.sampleRate);

    const mediaDeviceConstraints = {
      audio: {
        echoCancellation: true,
        autoGainControl: false,
        noiseSuppression: true,
        latency: 0,
      },
      video: false,
    };

    let micStream = await navigator.mediaDevices
      .getUserMedia(mediaDeviceConstraints)
      .then(async (stream) => {
        await audioRecorderContext.resume();
        return stream;
      })
      .catch((error) => {
        console.error("Error in Acessing Permissions for Microphone:", error);
        newRelicErrorLog(
          error,
          {
            dealershipId,
            recordingSessionId,
            originated: "startAudioRecorderWorklet",
          },
          "Error in Acessing Permissions for Microphone"
        );
      });

    if (!micStream) return [null, null, null, null];

    const source = audioRecorderContext.createMediaStreamSource(micStream);

    const workletURL = new URL("pcm-recorder-processor.js", import.meta.url);

    await audioRecorderContext.audioWorklet.addModule(workletURL);

    const audioRecorderNode = new AudioWorkletNode(
      audioRecorderContext,
      "pcm-recorder-processor"
    );

    audioRecorderNode.port.onmessage = (event) => {
      const { inputCopy: data, rms } = event.data;
      const outputSampleRate = 16000;

      const decreaseResultBuffer = decreaseSampleRate(
        data,
        audioRecorderContext.sampleRate,
        outputSampleRate
      );
      const pcmData = convertFloat32ToPCM(decreaseResultBuffer);

      let currentAudioStatus = null;
      rmsAccumulator.push(rms);

      const now = Date.now();

      if (now - lastAudioDetectionTime >= 200) {
        const avgRms =
          rmsAccumulator.reduce((a, b) => a + b, 0) / rmsAccumulator.length;

        const hasAudio = avgRms > 0.005;

        if (hasAudio) {
          currentAudioStatus = "active";
          lastAudioTime = now;
        } else {
          const silenceDuration = now - lastAudioTime;
          if (silenceDuration > 3000) {
            currentAudioStatus = "low-voice";
          }
        }
        rmsAccumulator = [];
        lastAudioDetectionTime = now;
      }
      audioRecorderHandler(pcmData, currentAudioStatus);
    };

    source.connect(audioRecorderNode);

    return [audioRecorderNode, audioRecorderContext, micStream, source];
  } catch (error) {
    console.error("Error in Audio Worklet", error);
    newRelicErrorLog(
      error,
      {
        dealershipId,
        recordingSessionId,
        originated: "startAudioRecorderWorklet",
      },
      "Error in Audio Worklet"
    );
  }
}

export async function stopMicrophone(
  micStream,
  audioRecorderNode,
  audioRecorderContext,
  source
) {
  if (micStream instanceof MediaStream) {
    micStream.getTracks().forEach((track) => track.stop());
  }

  if (audioRecorderNode instanceof AudioWorkletNode) {
    audioRecorderNode.port.onmessage = null;
    audioRecorderNode.disconnect();
  }

  if (source instanceof AudioNode) {
    source.disconnect();
  }

  if (audioRecorderContext instanceof window.AudioContext) {
    await audioRecorderContext.close();
  }

  await new Promise((r) => setTimeout(r, 50));

  rmsAccumulator = [];

  console.log("stopMicrophone(): Microphone stopped.");
}

function decreaseSampleRate(buffer, inputSampleRate, outputSampleRate) {
  if (inputSampleRate < outputSampleRate) {
    console.error("Sample rate too small.");
    return buffer;
  } else if (inputSampleRate === outputSampleRate) {
    return buffer;
  }

  let sampleRateRatio = inputSampleRate / outputSampleRate;
  let newLength = Math.ceil(buffer.length / sampleRateRatio);
  let result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    let nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0,
      count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = accum / count;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

function convertFloat32ToPCM(inputData) {
  const pcm16 = new Int16Array(inputData.length);
  for (let i = 0; i < inputData.length; i++) {
    // Multiply by 0x7fff (32767) to scale the float value to 16-bit PCM range.
    pcm16[i] = inputData[i] * 0x7fff;
  }
  return pcm16.buffer;
}
